import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, User, LogOut, Settings, Calendar, Play, HelpCircle } from 'lucide-react'
import useAuthStore from '../stores/authStore'

const NavigationHeader = () => {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const [manageDropdown, setManageDropdown] = useState(false)
  const [planDropdown, setPlanDropdown] = useState(false)
  const [designDropdown, setDesignDropdown] = useState(false)
  const [userDropdown, setUserDropdown] = useState(false)
  
  const manageRef = useRef(null)
  const planRef = useRef(null)
  const designRef = useRef(null)
  const userRef = useRef(null)

  const handleLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  const handleComingSoon = (e) => {
    e.preventDefault()
    alert('Coming soon!')
  }

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideManage = !manageRef.current?.contains(event.target)
      const isOutsidePlan = !planRef.current?.contains(event.target)
      const isOutsideDesign = !designRef.current?.contains(event.target)
      const isOutsideUser = !userRef.current?.contains(event.target)

      if (manageDropdown && isOutsideManage) {
        setManageDropdown(false)
      }
      if (planDropdown && isOutsidePlan) {
        setPlanDropdown(false)
      }
      if (designDropdown && isOutsideDesign) {
        setDesignDropdown(false)
      }
      if (userDropdown && isOutsideUser) {
        setUserDropdown(false)
      }
    }

    if (manageDropdown || planDropdown || designDropdown || userDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [manageDropdown, planDropdown, designDropdown, userDropdown])

  return (
    <>
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="w-full px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo - Far Left */}
            <div className="flex items-center">
              <button onClick={() => navigate('/home')} className="flex items-center">
                <img 
                  src="/sportigee-blue-no-background.png" 
                  alt="Sportigee" 
                  className="h-12 w-auto"
                />
              </button>
            </div>

            {/* Navigation Dropdowns - Right Aligned */}
            <nav className="flex items-center space-x-4">
              {/* Manage Dropdown */}
              <div className="relative" ref={manageRef}>
                <button
                  onClick={() => {
                    setManageDropdown(!manageDropdown)
                    setPlanDropdown(false)
                    setDesignDropdown(false)
                    setUserDropdown(false)
                  }}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <span>Manage</span>
                  <ChevronDown size={16} />
                </button>
                {manageDropdown && (
                  <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200 z-50">
                    <button onClick={handleComingSoon} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      My Account
                    </button>
                    <button onClick={handleComingSoon} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      My Organization
                    </button>
                    <button onClick={handleComingSoon} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      My Teams
                    </button>
                  </div>
                )}
              </div>

              {/* Plan Dropdown */}
              <div className="relative" ref={planRef}>
                <button
                  onClick={() => {
                    setPlanDropdown(!planDropdown)
                    setManageDropdown(false)
                    setDesignDropdown(false)
                    setUserDropdown(false)
                  }}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <span>Plan</span>
                  <ChevronDown size={16} />
                </button>
                {planDropdown && (
                  <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200 z-50">
                    <button
                      onClick={() => {
                        navigate('/practice-planner')
                        setPlanDropdown(false)
                      }}
                      className="flex items-center space-x-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Calendar size={16} />
                      <span>Practice Planner</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Design Dropdown */}
              <div className="relative" ref={designRef}>
                <button
                  onClick={() => {
                    setDesignDropdown(!designDropdown)
                    setManageDropdown(false)
                    setPlanDropdown(false)
                    setUserDropdown(false)
                  }}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <span>Design</span>
                  <ChevronDown size={16} />
                </button>
                {designDropdown && (
                  <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200 z-50">
                    <button
                      onClick={() => {
                        navigate('/soccer-designer')
                        setDesignDropdown(false)
                      }}
                      className="flex items-center space-x-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Play size={16} />
                      <span>Play Designer</span>
                    </button>
                  </div>
                )}
              </div>

              {/* User Profile Dropdown */}
              <div className="relative" ref={userRef}>
                <button
                  onClick={() => {
                    setUserDropdown(!userDropdown)
                    setManageDropdown(false)
                    setPlanDropdown(false)
                    setDesignDropdown(false)
                  }}
                  className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <User size={18} />
                  <span className="hidden sm:inline">{user?.username || 'User'}</span>
                  <ChevronDown size={16} />
                </button>
                {userDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200 z-50">
                    <button onClick={handleComingSoon} className="flex items-center space-x-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <Settings size={16} />
                      <span>Profile Settings</span>
                    </button>
                    <button onClick={handleComingSoon} className="flex items-center space-x-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <HelpCircle size={16} />
                      <span>Help & Support</span>
                    </button>
                    <div className="border-t border-gray-200 my-1"></div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                    >
                      <LogOut size={16} />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </nav>
          </div>
        </div>
      </header>
    </>
  )
}

export default NavigationHeader

